import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { UserCog } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CopilotPanel from "@/components/CopilotPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const AppContext = createContext({});
export const useApp = () => useContext(AppContext);

export default function Layout() {
  const { auth, stopImpersonation } = useAuth();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [menuMobile, setMenuMobile] = useState(false);
  const [stats, setStats] = useState(null);
  const [nbSuggestions, setNbSuggestions] = useState(0);
  const [copilotEnLigne, setCopilotEnLigne] = useState(true);

  const refreshStats = useCallback(() => {
    api.get("/stats/activite").then((r) => setStats(r.data)).catch(() => {});
    api.get("/copilot/suggestions?module=all").then((r) => {
      setNbSuggestions(r.data.total);
      setCopilotEnLigne(r.data.en_ligne);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshStats();
    const t = setInterval(refreshStats, 60000);
    return () => clearInterval(t);
  }, [refreshStats]);

  return (
    <AppContext.Provider value={{ stats, refreshStats, nbSuggestions, copilotEnLigne, setCopilotOpen }}>
      <div className="flex min-h-screen">
        <Sidebar stats={stats} ouvert={menuMobile} onClose={() => setMenuMobile(false)} />
        {menuMobile && (
          <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMenuMobile(false)} data-testid="sidebar-overlay" />
        )}
        <div className="flex-1 flex flex-col min-w-0 lg:ml-[232px]">
          {auth?.impersonation && (
            <div className="flex items-center justify-center gap-3 px-4 py-2 text-[12.5px] font-medium text-white flex-wrap text-center" style={{ background: "#B45309" }} data-testid="impersonation-banner">
              <UserCog className="w-4 h-4" />
              Vous êtes connecté en tant que <b>{auth.user?.nom}</b> (usurpation administrateur)
              <button onClick={stopImpersonation} className="underline font-semibold hover:opacity-80" data-testid="impersonation-retour-btn">
                Revenir à mon compte
              </button>
            </div>
          )}
          <Header
            onCopilot={() => setCopilotOpen((v) => !v)}
            onMenu={() => setMenuMobile(true)}
            nbSuggestions={nbSuggestions}
            enLigne={copilotEnLigne}
          />
          <main className="flex-1 px-4 sm:px-7 py-5 sm:py-6 max-w-[1600px] w-full mx-auto">
            <Outlet />
          </main>
        </div>
        <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} enLigne={copilotEnLigne} />
      </div>
    </AppContext.Provider>
  );
}
